import React from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { hot } from "react-hot-loader/root";
import "react-datepicker/dist/react-datepicker.css";
import { Box } from "@mui/material";
import Loading from "../../shared/Loading";
import AppMenu from "./layout/AppMenu";
import { ErrorBoundary } from "../ErrorBoundary";
import { PatientData } from "./Contexts";
import Flash from "./layout/Flash";
import * as API from "./api";
import * as CONST from "./Constants";
import Reducer from "./Reducer";
import { useStyles } from "./layout/Styles";
import { useHistory } from "react-router-dom";
import SelectedLocation from "./pages/dashboard/SelectedLocation";
import Dashboard from "./pages/Dashboard";
import PcpElectionPage from "./pages/PcpElectionPage";
import GeneralSupportPage from "./pages/GeneralSupportPage";
import SpecialistReferralsPage from "./pages/SpecialistReferralsPage";
import AdditionalMessagesPage from "./pages/AdditionalMessagesPage";
import CompletedMessagePage from "./pages/CompletedMessagePage";
import CprStatusPage from "./pages/CprStatusPage";
import AnnualWellnessVisitPage from "./pages/AnnualWellnessVisitPage";
import CprNotificationsPage from "./pages/CprNotificationsPage";
import InfoCenterPage from "./pages/InfoCenterPage";
import OnboardingPage from "./pages/OnboardingPage";
import ReactGA from 'react-ga';

ReactGA.initialize('G-L3VSVT44GL', []);

const propTypes = {
  window: () => Window,
};

PatientApp.prototype = propTypes;

function PatientApp() {
  const [state, dispatch] = React.useReducer(Reducer, CONST.INIT_STATE);
  const [data, setData] = React.useState(null);
  const [flash, setFlash] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedPractice, setSelectedPractice] = React.useState(null);

  const providerPatientData = React.useMemo(() => ({ ...data, setData }), [data, setData]);
  const [fetching, setFetching] = React.useState({ in_progress: false, completed: false });
  const realHistory = useHistory();

  const patientData = async () => {
    const patientData = await API.getPatientData();
    setData(patientData);
    dispatch({ type: "set_data", payload: patientData });
  };

  async function completedCprs() {
    let page = state.completed_cprs.page + 1;
    setFetching({ ...fetching, completed: true });
    let data = await API.getClosedCprs(data);
    dispatch({
      type: "set_completed",
      payload: data,
    });
    dispatch({
      type: "set_completed_cprs_page_and_count",
      payload: {
        page: page,
        total: data.total_count,
        fetched: !data.cprs.length || data.cprs.length == data.total_count ? true : false,
        count: state.completed_cprs.length + data.cprs.length,
      },
    });
    setFetching({ ...fetching, completed: false });
  }

  async function cprs() {
    setFetching({ ...fetching, in_progress: true });
    let page = state.in_progress.page + 1;
    let data = await API.getAllCprsInProgress(page);
    dispatch({
      type: "set_in_progress_cprs_page_and_count",
      payload: {
        page: page,
        total: data.total_count,
        fetched: !data.cprs.length || data.cprs.length == data.total_count ? true : false,
        count: state.cprs.length + data.cprs.length,
      },
    });
    dispatch({ type: "set_all_cprs", payload: data });
    setFetching({ ...fetching, in_progress: false });
  }

  React.useEffect(() => {
    (async () => {
      await patientData();
      ReactGA.pageview(window.location.pathname);
      if (!state.cprs) {
        const data = await API.getAllCprsInProgress(1);
        dispatch({ type: "set_initial_cprs", payload: data });
        dispatch({
          type: "set_in_progress_cprs_page_and_count",
          payload: {
            page: 1,
            fetched: !data.cprs.length || data.cprs.length == data.total_count ? true : false,
            total: data.total_count,
            count: data.cprs.length,
          },
        });
      }
      if (!state.notifications) {
        const notifications = await API.getCprsWithNotifications();
        if (notifications.total_count !== 0) {
          dispatch({
            type: "set_notifications",
            payload: notifications,
          });
        }
      }

      if (!state.completed_cprs) {
        const data = await API.getClosedCprs(1);
        dispatch({
          type: "set_completed",
          payload: data,
        });
        dispatch({
          type: "set_completed_cprs_page_and_count",
          payload: {
            page: 1,
            fetched: !data.cprs.length || data.cprs.length == data.total_count ? true : false,
            total: data.total_count,
            count: data.cprs.length,
          },
        });
      }

      const supportMessages = await API.getSupportMessages();
      if (supportMessages.task_messages) {
        dispatch({ type: "set_support_messages", payload: supportMessages.task_messages });
      }

      if (!state.messages) {
        const messages = await API.getMessages(1);
        if (messages) {
          dispatch({ type: "set_initial_messages", payload: messages });
        }
      }

      setTimeout(() => setLoading(false), 200);
    })();
  }, []);

  const appProps = {
    scrollToView: (elem, timeOnScroll) => {
      const anchor = document.querySelector(elem);
      setTimeout(() => {
        if (anchor) {
          anchor.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, timeOnScroll);
    },

    dispatch: dispatch,
    loading: loading,
    fetching: { ...fetching },
    fetch: {
      cprs: () => cprs(),
      patientData: () => patientData(),
      completed: () => completedCprs(),
    },
  };

  function renderLayout(children) {
    return (
      <Box id="patient-portal-container">
        {loading ? (
          <Loading message="loading" />
        ) : (
          <AppMenu
            className={styles.appMenu}
            {...appProps}
            messages={state.messages}
            total_messages={state.total_messages}
            support_messages={state.support_messages}
            specialist_referrals={state.specialist_referrals}
            notifications={state.notifications}
            alerts={state.patient_portal_alerts}
            old={state.old}
            cprs={state.cprs}
            currentCpr={state.current}
            completed_cprs={state.completed_cprs}
            in_progress={state.in_progress}
            completed={state.completed}
          >
            {children}
          </AppMenu>
        )}
      </Box>
    );
  }
  const styles = useStyles();
  const onSubmitPcp = async (req) => {
    let request;

    if (req.id) {
      request = { pcp_id: req.id };
    } else {
      request = {
        ooa_pcp: {
          ...req,
        },
      };
    }
    request['pcp_practice_id'] = req.pcp_practice_id;

    let res = await API.postToPatientSettings(request);
    if (res.error) {
      setFlash(res);
    } else {
      appProps.fetch.patientData();
      setFlash({
        success: !req.id ? CONST.FLASH.PCP_ELECTED_OUT_OF_AREA : CONST.FLASH.PCP_ELECTED,
      });
    }
  };
  return (
    <>
      {flash && <Flash flash={flash} handleClose={() => setFlash(null)} />}

      <BrowserRouter>
        <ErrorBoundary>
          <PatientData.Provider value={providerPatientData}>
            <Switch>
              <Route
                path={"/patient_portal/pcp_election"}
                render={() => renderLayout(<PcpElectionPage {...appProps} />)}
              />

              <Route
                path={"/patient_portal/annual_wellness_visit"}
                render={() => renderLayout(<AnnualWellnessVisitPage {...appProps} />)}
              />

              <Route
                path={"/patient_portal/annual_wellness_visit_2026"}
                render={() => renderLayout(<AnnualWellnessVisitPage {...appProps} year={'2026'} />)}
              />

              <Route
                path={"/patient_portal/annual_wellness_visit_2025"}
                render={() => renderLayout(<AnnualWellnessVisitPage {...appProps} year={'2025'} />)}
              />

              <Route
                path={"/patient_portal/practice/:id"}
                render={() => renderLayout(
                  <SelectedLocation
                    {...appProps}
                    dispatch={dispatch}
                    patientData={data}
                    pcp_practice={state.pcp_practice}
                    selectPcp={onSubmitPcp}
                    selectHandler={() => setSelectedPractice(null)}
                  />
                )}
              />

              <Route
                path={"/patient_portal/dashboard"}
                render={() =>
                  renderLayout(
                    <Dashboard
                      {...appProps}
                      in_progress={state.in_progress}
                      cprs={state.cprs}
                      notifications={state.notifications}
                    />
                  )
                }
              />

              <Route
                path={"/patient_portal/notifications/:id"}
                render={() =>
                  renderLayout(
                    <CprNotificationsPage
                      {...appProps}
                      cpr={state.current}
                      notifications={state.notifications}
                      additional_messages={state.messages}
                    />
                  )
                }
              />
              <Route
                path={"/patient_portal/requirement/:id"}
                render={() =>
                  renderLayout(
                    <CprStatusPage
                      current={state.current}
                      notifications={state.notifications}
                      {...appProps}
                    />
                  )
                }
              />
              <Route
                path={"/patient_portal/additional_messages/:id"}
                render={() =>
                  !state.loading &&
                  renderLayout(
                    <AdditionalMessagesPage
                      messages={state.messages}
                      old={state.old}
                      cpr_notifications={state.notifications}
                      active={state.active_message}
                      dispatch={dispatch}
                      state={state}
                    />
                  )
                }
              />
              <Route
                path={"/patient_portal/message/:id"}
                render={() =>
                  renderLayout(<CompletedMessagePage old={state.old} dispatch={dispatch} />)
                }
              />
              <Route
                path={"/patient_portal/onboarding"}
                render={() =>
                  renderLayout(
                    <OnboardingPage
                      notifications={state.notifications}
                      setData={(step) => setData({ ...data, onboarding_step: step })}
                    />
                  )
                }
              />
              <Route
                path={"/patient_portal/info_center"}
                render={() => renderLayout(<InfoCenterPage />)}
              />
              <Route
                path={"/patient_portal/general_support"}
                render={() =>
                  renderLayout(
                    <GeneralSupportPage dispatch={dispatch} />
                  )
                }
              />
              <Route
                path={"/patient_portal/specialist_referrals"}
                render={() =>
                  renderLayout(
                    <SpecialistReferralsPage dispatch={dispatch} />
                  )
                }
              />
            </Switch>
          </PatientData.Provider>
        </ErrorBoundary>
      </BrowserRouter>
    </>
  );
}

export default hot(PatientApp);
